/* ============================================================
   Lab Effects — MOBILE SCROLL PERFORMANCE
   assets/le-mobile-perf.js

   WHAT WAS WRONG (measured on staging, /collections/all at 375px):

     654 infinite CSS animations running at once
     109 twist rigs x 6 animations each
     107 of those 109 rigs were OFF SCREEN
       2 were actually visible
     218 elements animating a BLURRED layer — blur(7px) and blur(18px)

   Every twist card runs six infinite animations, two of which animate an
   element carrying a CSS blur filter. A blurred layer has to be re-rasterised
   as it moves, so those 218 layers were being repainted continuously, forever,
   for the whole length of the collection — while ~98% of them were nowhere
   near the viewport. That is what made scrolling stutter and stall on a phone
   and left images and backgrounds unpainted for seconds: the compositor never
   got an idle moment to do anything else.

   THE FIX — do the work only where it is visible:

     1. Pause every twist rig that is off screen (animation-play-state), so the
        count drops from 654 to roughly a dozen. The rig is invisible when
        paused by definition, so nothing about the design changes.
     2. content-visibility on the collection cards, so the browser skips
        layout and paint for the ~400 cards that are off screen.

   MOBILE ONLY, to honour the "do not touch desktop" rule — though desktop
   runs the same 654 animations and would benefit identically.
   ============================================================ */
(function () {
  'use strict';

  var MQ = window.matchMedia('(max-width: 767px)');
  if (!MQ.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var PAUSED = 'le-anim-paused';

  /* rootMargin gives a screen of runway either side, so a rig is already
     running by the time it scrolls into view and never appears mid-pose. */
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      e.target.classList.toggle(PAUSED, !e.isIntersecting);
    });
  }, { rootMargin: '120% 0px' });

  var seen = new WeakSet();

  /* Measure rather than assume for the INITIAL state.

     Setting every rig paused at observe() time and waiting for the observer to
     switch the visible ones back on does not work: that first callback is
     delivered before layout has settled on a 400-card page, so rigs that are
     plainly on screen — measured at top 317 and top 704 in an 812px viewport —
     stayed paused and the cards the shopper was actually looking at sat frozen.

     So the initial state is computed from the element's own box, and the
     observer only handles CHANGES from there. Same margin as the observer's
     rootMargin, so the two agree. */
  function nearViewport(el) {
    var r = el.getBoundingClientRect();
    var m = window.innerHeight * 1.2;
    return r.bottom > -m && r.top < window.innerHeight + m;
  }

  function observeAll() {
    var rigs = document.querySelectorAll('.le-twist');
    for (var i = 0; i < rigs.length; i++) {
      if (seen.has(rigs[i])) continue;
      seen.add(rigs[i]);
      rigs[i].classList.toggle(PAUSED, !nearViewport(rigs[i]));
      io.observe(rigs[i]);
    }
  }

  /* One re-sync after load, for anything whose box moved as images sized
     themselves and whose observer callback has not caught up yet. */
  function resync() {
    var rigs = document.querySelectorAll('.le-twist');
    for (var i = 0; i < rigs.length; i++) {
      rigs[i].classList.toggle(PAUSED, !nearViewport(rigs[i]));
    }
  }

  observeAll();
  window.addEventListener('load', function () { resync(); setTimeout(resync, 600); });

  /* The collection background-loads the rest of its pages and can swap its
     whole region on a category switch, so new rigs appear after load. Observing
     the tree is what keeps them covered without this file knowing when. */
  var mo = new MutationObserver(function () { observeAll(); });
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('DOMContentLoaded', observeAll);
  window.addEventListener('lab:collection-switched', observeAll);

  /* ============================================================
     WARM UPCOMING IMAGES

     `loading="lazy"` only starts fetching once an image is close to the
     viewport, and the browser deprioritises that work while a fling is in
     progress — so cards arrive blank and only fill in once the scroll stops.
     Reversing direction is the worst case, because everything ahead is
     freshly out of the lazy-loading window again.

     This walks ahead of the shopper by two screens IN BOTH DIRECTIONS
     (rootMargin is symmetric) and, for each card image entering that band,
     promotes it out of lazy loading and asks the browser to decode it. By the
     time it is on screen the bitmap is already decoded, so it paints on the
     frame it appears rather than after the scroll settles.

     decode() is a promise and its rejection is meaningless here (an image that
     is removed or replaced mid-flight rejects), so failures are swallowed.
     ============================================================ */
  var warmed = new WeakSet();

  /* RECT-DRIVEN, not IntersectionObserver.

     An observer is the wrong tool for this specific job: its callbacks are
     DEFERRED during a fast fling, which is precisely the moment the shopper
     outruns the images. Using one here would reproduce the very symptom it is
     meant to fix — blank cards until you stop moving. Measuring rects on
     scroll keeps working at any speed.

     Bounded per pass so a 500-card grid cannot turn one scroll tick into a
     thousand rect reads: it stops after a batch and picks the rest up on the
     next tick, which is only milliseconds later. */
  var WARM_SEL = '.lab-col-grid img, .le-mhome__rail img, .le-mrel img';
  var WARM_BATCH = 24;

  function warmPass() {
    warmRaf = null; warmTimer = null;
    var band = window.innerHeight * 2;      // two screens either way
    var imgs = document.querySelectorAll(WARM_SEL);
    var done = 0;
    for (var i = 0; i < imgs.length && done < WARM_BATCH; i++) {
      var img = imgs[i];
      if (warmed.has(img)) continue;
      var r = img.getBoundingClientRect();
      if (r.bottom < -band || r.top > window.innerHeight + band) continue;
      warmed.add(img);
      done++;
      // Out of lazy loading so the fetch is not deferred any further, then
      // decode up front so it paints on the frame it appears rather than
      // after the scroll settles. A removed or swapped image rejects; that is
      // meaningless here.
      if (img.loading === 'lazy') img.loading = 'eager';
      if (img.decode) img.decode().catch(function () {});
    }
  }

  /* rAF AND a setTimeout backstop — rAF does not fire in a backgrounded tab
     and gets starved under exactly the load this is meant to relieve. */
  var warmRaf = null, warmTimer = null;
  function queueWarm() {
    if (warmRaf || warmTimer) return;
    if (window.requestAnimationFrame) warmRaf = requestAnimationFrame(warmPass);
    warmTimer = setTimeout(warmPass, 120);
  }

  window.addEventListener('scroll', queueWarm, { passive: true });
  window.addEventListener('resize', queueWarm);
  document.addEventListener('DOMContentLoaded', queueWarm);
  window.addEventListener('lab:collection-switched', queueWarm);
  queueWarm();
  /* The grid grows a page at a time, so new images keep arriving. */
  var warmMo = new MutationObserver(queueWarm);
  if (document.body) warmMo.observe(document.body, { childList: true, subtree: true });

  /* ============================================================
     CARD BACKGROUND VIDEO — download late, play only when seen

     The file is ~2.6MB, which is more than the whole optimised collection
     page holds in decoded images, so it must never be fetched speculatively.
     preload="none" in the markup means nothing moves until load() is called
     here, and that only happens once the card is within a screen of the
     viewport.

     Video decode is hardware-accelerated, so this is a very different cost
     from the CSS blur animations that were melting the compositor earlier —
     but a video playing off-screen is still pure waste, so it pauses the
     moment it leaves.

     Rect-driven on scroll for the same reason the image warming is: observer
     callbacks are deferred during a fling, and rAF is starved under load.
     ============================================================ */
  var vidStarted = new WeakSet();
  var fbArmed = new WeakSet();

  /* ---------- video failure fallback ----------
     The card's photo is hidden (see .has-fb in le-mobile-home.css) and only
     revealed when the video genuinely does not come up. Deliberately NOT the
     <video poster> attribute: a poster shows until the first frame decodes,
     which with preload="none" means it is on screen the entire time the card
     is waiting to be scrolled to. The brief was the opposite — show it only
     when something is wrong.

     Three things count as wrong, and they are the three ways this actually
     fails in the wild: an error event (404, bad codec), a rejected play()
     (autoplay blocked, Low Power Mode), and four seconds on screen with no
     decoded frame (dead or crawling network). Anything that later starts
     playing hides it again, so a slow connection that eventually succeeds
     does not leave a stale still frame on the card. */
  function card(v) { return v.closest ? v.closest('.fmt') : null; }
  function showFallback(v) { var c = card(v); if (c) c.classList.add('fb-on'); }
  function hideFallback(v) { var c = card(v); if (c) c.classList.remove('fb-on'); }

  function armFallback(v) {
    if (fbArmed.has(v)) return;
    fbArmed.add(v);
    v.addEventListener('error', function () { showFallback(v); });
    v.addEventListener('stalled', function () { if (v.readyState < 2) showFallback(v); });
    // HAVE_CURRENT_DATA means a frame exists, so the card is showing video.
    v.addEventListener('playing', function () { hideFallback(v); });
    v.addEventListener('loadeddata', function () { hideFallback(v); });
    setTimeout(function () { if (v.readyState < 2) showFallback(v); }, 4000);
  }

  function videoPass() {
    var vids = document.querySelectorAll('[data-le-cardvideo]');
    if (!vids.length) return;
    /* Not before the page has laid out. On the first pass the rails have not
       sized themselves yet, so every card measures near the top and the whole
       point of preload="none" is lost — the fetch fires immediately for a card
       that is thousands of pixels down. Waiting for `complete` means the
       measurement is of the real layout. */
    if (document.readyState !== 'complete') return;
    var band = window.innerHeight * 0.5;    // half a screen of runway
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      var r = v.getBoundingClientRect();
      var near = r.bottom > -band && r.top < window.innerHeight + band;
      var onScreen = r.bottom > 0 && r.top < window.innerHeight;

      if (near && !vidStarted.has(v)) {
        vidStarted.add(v);
        // preload="none" means the source is not fetched until asked.
        try { v.load(); } catch (e) {}
        armFallback(v);
      }
      if (onScreen) {
        if (v.paused) {
          var p = v.play();
          // A rejected play() is a blocked autoplay — a real failure, not a wait.
          if (p && p.catch) p.catch(function () { showFallback(v); });
        }
      } else if (!v.paused) {
        v.pause();
      }
    }
  }

  var vidRaf = null, vidTimer = null;
  function queueVideo() {
    if (vidRaf || vidTimer) return;
    if (window.requestAnimationFrame) vidRaf = requestAnimationFrame(function () { vidRaf = null; vidTimer = null; videoPass(); });
    vidTimer = setTimeout(function () { vidRaf = null; vidTimer = null; videoPass(); }, 150);
  }

  window.addEventListener('scroll', queueVideo, { passive: true });
  window.addEventListener('resize', queueVideo);
  window.addEventListener('load', queueVideo);
  /* A backgrounded tab should not keep a video running. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      document.querySelectorAll('[data-le-cardvideo]').forEach(function (v) { if (!v.paused) v.pause(); });
    } else { queueVideo(); }
  });
})();